import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Brain, X, Copy } from 'lucide-react';
import { renderField } from './renderField';

// Pure presentational diagnosis card. Owns no state — receives the diagnosis
// payload and a copy-code callback from the parent.
export function DiagnosisCard({ diagnosis, onClose, onCopyCode }) {
  return (
    <Card
      className="p-6 bg-gradient-to-br from-teal-50 to-cyan-50 backdrop-blur-xl rounded-3xl border border-purple-200 shadow-xl"
      data-testid="diagnosis-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Comprehensive Diagnostic Evaluation</h3>
        </div>
        <Button onClick={onClose} variant="ghost" size="sm" className="rounded-full">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        <div>
          <p className="text-xs font-semibold text-purple-700 uppercase mb-1">Primary Diagnosis</p>
          <p className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl leading-relaxed">{renderField(diagnosis.diagnosis)}</p>
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Severity</p>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
              typeof diagnosis.severity === 'string' && diagnosis.severity.toLowerCase().includes('severe') ? 'bg-red-100 text-red-700' :
              typeof diagnosis.severity === 'string' && diagnosis.severity.toLowerCase().includes('moderate') ? 'bg-amber-100 text-amber-700' :
              'bg-green-100 text-green-700'
            }`}>
              {renderField(diagnosis.severity)}
            </span>
          </div>
          {diagnosis.prognosis && (
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Prognosis</p>
              <p className="text-xs text-gray-800 bg-white/60 p-2 rounded-lg">{renderField(diagnosis.prognosis)}</p>
            </div>
          )}
        </div>

        {diagnosis.diagnostic_criteria && (
          <div>
            <p className="text-xs font-semibold text-blue-700 uppercase mb-1">DSM-5 Criteria Met</p>
            <p className="text-sm text-gray-900 bg-blue-50/60 p-3 rounded-xl leading-relaxed whitespace-pre-wrap">{renderField(diagnosis.diagnostic_criteria)}</p>
          </div>
        )}

        {diagnosis.differential_diagnosis && (
          <div>
            <p className="text-xs font-semibold text-amber-700 uppercase mb-1">Differential Diagnosis</p>
            <p className="text-sm text-gray-900 bg-amber-50/60 p-3 rounded-xl leading-relaxed whitespace-pre-wrap">{renderField(diagnosis.differential_diagnosis)}</p>
          </div>
        )}

        {diagnosis.clinical_formulation && (
          <div>
            <p className="text-xs font-semibold text-indigo-700 uppercase mb-1">Clinical Formulation (Biopsychosocial)</p>
            <p className="text-sm text-gray-900 bg-indigo-50/60 p-3 rounded-xl leading-relaxed whitespace-pre-wrap">{renderField(diagnosis.clinical_formulation)}</p>
          </div>
        )}

        {diagnosis.risk_factors && (
          <div>
            <p className="text-xs font-semibold text-red-700 uppercase mb-1">Risk & Protective Factors</p>
            <p className="text-sm text-gray-900 bg-red-50/60 p-3 rounded-xl leading-relaxed whitespace-pre-wrap">{renderField(diagnosis.risk_factors)}</p>
          </div>
        )}

        {diagnosis.recommendations && (
          <div>
            <p className="text-xs font-semibold text-green-700 uppercase mb-1">Treatment Recommendations</p>
            <p className="text-sm text-gray-900 bg-green-50/60 p-3 rounded-xl leading-relaxed whitespace-pre-wrap">{renderField(diagnosis.recommendations)}</p>
          </div>
        )}

        {diagnosis.treatment_plan_summary && (
          <div>
            <p className="text-xs font-semibold text-teal-700 uppercase mb-1">Treatment Plan Summary</p>
            <p className="text-sm text-gray-900 bg-teal-50/60 p-3 rounded-xl leading-relaxed whitespace-pre-wrap">{renderField(diagnosis.treatment_plan_summary)}</p>
          </div>
        )}

        {(diagnosis.icd10_codes?.length > 0 || diagnosis.cpt_codes?.length > 0) && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">Billing Codes</p>
            {diagnosis.icd10_codes?.length > 0 && (
              <div className="mb-2">
                <p className="text-xs text-gray-600 mb-1">ICD-10:</p>
                <div className="space-y-1">
                  {diagnosis.icd10_codes.map((code, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white rounded p-1.5 text-xs">
                      <span className="font-mono font-semibold text-green-700">{code.code}</span>
                      <span className="text-gray-700 flex-1">{code.description}</span>
                      <button onClick={() => onCopyCode(code.code, 'Code')} className="text-green-600 hover:text-green-700"><Copy className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {diagnosis.cpt_codes?.length > 0 && (
              <div>
                <p className="text-xs text-gray-600 mb-1">CPT:</p>
                <div className="space-y-1">
                  {diagnosis.cpt_codes.map((code, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white rounded p-1.5 text-xs">
                      <span className="font-mono font-semibold text-blue-700">{code.code}</span>
                      <span className="text-gray-700 flex-1">{code.description}</span>
                      <button onClick={() => onCopyCode(code.code, 'Code')} className="text-blue-600 hover:text-blue-700"><Copy className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <p className="text-xs text-gray-500 italic bg-white/40 p-2 rounded-lg">
          This comprehensive evaluation is AI-generated. Clinical judgment should always guide final diagnostic and treatment decisions.
        </p>
      </div>
    </Card>
  );
}
